import {
  Badge,
  Card,
  Cell,
  Column,
  EmptyState,
  Field,
  FieldGrid,
  Row,
  Table,
  TableBody,
  TableContainer,
  TableFrame,
  type TableFramePagination,
  TableHeader,
  RowExpandButton,
  TabPills,
} from '@skupreme/shared/ui';
import { ItemProductCell } from '@features/catalog';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';
import { MapPinIcon, PlugIcon } from '@phosphor-icons/react';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';
import { getProviderIntegrationName } from '../../lib/integration';
import {
  providerBrandName,
  providerNodeCountries,
} from '../../lib/provider-brand';
import type {
  StorageProviderV2Detail,
  StorageProviderV2InventoryItem,
  StorageProviderV2NodeAddress,
} from '../../types-v2';
import { ProviderSummaryCard } from '../page/ProviderSummary';
import { RoutingPausedBadge, routingPausedText } from './RoutingPausedBadge';
import { providerStatus } from '../../lib/provider-status';

/* As the prototype: Details (the connection's state is part of it) and Inventory. */
export const DETAIL_V2_TABS = [
  { id: 'details', label: 'Details' },
  { id: 'inventory', label: 'Inventory' },
] as const;

const HEALTH_EVENT_LIMIT = 20;

const when = (date: string | null) =>
  DateUtility.formatDate(date, DateFormat.dateTime);

/* The parts that are present, joined. */
const joined = (...parts: (string | null | undefined)[]) =>
  parts.filter(Boolean).join(' - ');

/** A value, or a dash when there is none. */
function Value({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <Field label={label}>
      {children === '' || children == null ? '-' : children}
    </Field>
  );
}

/** The summary card as the prototype's: the brand's mark, the name, the status, the kind and the units held. */
function SummaryV2({ detail }: { detail: StorageProviderV2Detail }) {
  const { provider, warehouses, nodes } = detail;
  const kind =
    provider.brand_name ??
    providerBrandName(provider.provider) ??
    getProviderIntegrationName(provider.provider);

  return (
    <ProviderSummaryCard
      name={provider.display_name}
      logoUrl={provider.logo_url ?? undefined}
      countryCode={providerNodeCountries(nodes, warehouses)[0]}
    >
      <StatusBadge status={provider.status} />
      {kind ? <Badge>{kind}</Badge> : null}
      <Badge>{CommonUtility.numberWithCommas(detail.total_units)} units</Badge>
    </ProviderSummaryCard>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { label, tone } = providerStatus(status);
  return <Badge tone={tone}>{label}</Badge>;
}

/* Every country the nodes' addresses (else the warehouses) are in, by name. */
const countryNames = (
  nodes: StorageProviderV2Detail['nodes'],
  warehouses: StorageProviderV2Detail['warehouses'],
) =>
  providerNodeCountries(nodes, warehouses)
    .map(code => {
      try {
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
      } catch {
        return code;
      }
    })
    .join(', ');

/* A card's fields, one under another, as the prototype's section cards. */
function FieldList({ children }: { children: ReactNode }) {
  return <div className="sk:flex sk:flex-col sk:gap-3">{children}</div>;
}

/**
 * As the prototype: Business Details and Connection side by side (Contact Person too, once V2 holds a contact), then the
 * connection's warehouses and nodes, and health events. Everything V2 holds about the provider, on one tab.
 */
function DetailsV2({ detail }: { detail: StorageProviderV2Detail }) {
  const { provider, warehouses, nodes, health_events: events } = detail;
  const kind =
    provider.brand_name ??
    providerBrandName(provider.provider) ??
    getProviderIntegrationName(provider.provider);
  const names = (active: boolean) =>
    warehouses
      .filter(warehouse => warehouse.is_active === active)
      .map(warehouse => warehouse.name)
      .join(', ');
  const transition = provider.last_health_to
    ? `${provider.last_health_from ?? '-'} → ${provider.last_health_to}`
    : '';
  const outcome = provider.last_call_outcome
    ? joined(provider.last_call_outcome, provider.last_call_error_class)
    : '';

  return (
    <div className="sk:flex sk:flex-col sk:gap-4">
      <div className="sk:grid sk:grid-cols-2 sk:items-start sk:gap-4">
        <Card title="Business Details" icon={<MapPinIcon size={14} />}>
          <FieldList>
            <Value label="Country">{countryNames(nodes, warehouses)}</Value>
            <Value label="WMS Provider">{kind || provider.provider}</Value>
            <Value label="Mode">{provider.mode}</Value>
            <Value label="Label buyer">{provider.label_buyer}</Value>
            <Value label="Active Warehouses">{names(true)}</Value>
            <Value label="Inactive Warehouses">{names(false)}</Value>
            <Value label="Nodes">{nodes.length || provider.node_count}</Value>
          </FieldList>
        </Card>
        <Card title="Connection" icon={<PlugIcon size={14} />}>
          <FieldList>
            <Value label="Connected Since">{when(provider.created_at)}</Value>
            <Value label="Last Updated">{when(provider.updated_at)}</Value>
            <Value label="Status">
              <StatusBadge status={provider.status} />
            </Value>
            <Value label="Credential on file">
              {provider.has_credential ? 'Yes' : 'No'}
            </Value>
            <Value label="Last health change">
              {joined(
                transition,
                provider.last_health_reason,
                when(provider.last_health_at),
              )}
            </Value>
            <Value label="Last call">
              {joined(outcome, when(provider.last_call_at))}
            </Value>
            <Value label="Last sent">{when(provider.last_sent_at)}</Value>
            <Value label="Calls in 24h">
              {`${provider.calls_ok_24h} ok, ${provider.calls_error_24h} with error`}
            </Value>
            <Value label="Open intents">{provider.open_intents}</Value>
          </FieldList>
        </Card>
      </div>
      <ConnectionTables nodes={nodes} events={events} />
    </div>
  );
}

/** EN-4544: a node's facility address on one line, for the nodes table; empty when the node has none. */
const addressLine = (
  address: StorageProviderV2NodeAddress | null | undefined,
) =>
  address
    ? [
        ...(address.address_lines ?? []),
        address.locality,
        [address.administrative_area, address.postal_code]
          .filter(Boolean)
          .join(' '),
        address.country_code,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

/** The connection's warehouses and nodes, and its latest health events. */
function ConnectionTables({
  nodes,
  events,
}: {
  nodes: StorageProviderV2Detail['nodes'];
  events: StorageProviderV2Detail['health_events'];
}) {
  return (
    <div className="sk:flex sk:flex-col sk:gap-4">
      <Card title="Warehouses / Nodes" bodyClassName="sk:p-0">
        <TableContainer>
          <Table aria-label="Warehouses / Nodes">
            <TableHeader>
              <Column id="code" isRowHeader className="sk:w-40">
                Code
              </Column>
              <Column id="name">Warehouse / Node</Column>
              <Column id="kind" className="sk:w-40">
                Kind
              </Column>
              <Column id="address">Address</Column>
            </TableHeader>
            <TableBody
              renderEmptyState={() => (
                <div className="sk:px-3 sk:py-8 sk:text-center sk:text-fg-tertiary">
                  No nodes
                </div>
              )}
            >
              {nodes.map(node => (
                <Row key={node.id} id={node.id}>
                  <Cell>{node.code}</Cell>
                  <Cell>
                    <span className="sk:inline-flex sk:items-center sk:gap-2">
                      {node.name}
                      <RoutingPausedBadge pause={node} />
                    </span>
                  </Cell>
                  <Cell>{node.kind}</Cell>
                  <Cell>{addressLine(node.address) || '-'}</Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Card title="Health events" bodyClassName="sk:p-0">
        <TableContainer>
          <Table aria-label="Health events">
            <TableHeader>
              <Column id="at" isRowHeader className="sk:w-48">
                When
              </Column>
              <Column id="from" className="sk:w-40">
                From
              </Column>
              <Column id="to" className="sk:w-40">
                To
              </Column>
              <Column id="reason">Reason</Column>
            </TableHeader>
            <TableBody
              renderEmptyState={() => (
                <div className="sk:px-3 sk:py-8 sk:text-center sk:text-fg-tertiary">
                  No health events
                </div>
              )}
            >
              {events.slice(0, HEALTH_EVENT_LIMIT).map(event => (
                <Row
                  key={`${event.at}:${event.to_status}`}
                  id={`${event.at}:${event.to_status}`}
                >
                  <Cell>{when(event.at)}</Cell>
                  <Cell>{event.from_status}</Cell>
                  <Cell>{event.to_status}</Cell>
                  <Cell>{event.reason}</Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </div>
  );
}

/** A count, or a dash when the provider did not report one. */
const count = (value: number | null) =>
  value == null ? '-' : CommonUtility.numberWithCommas(value);

export type InventoryV2Data = {
  items: StorageProviderV2InventoryItem[];
  loading: boolean;
  error: string;
  pagination: TableFramePagination;
};

type InventoryItem = StorageProviderV2InventoryItem;
type InventoryWarehouse = InventoryItem['warehouses'][number];
type Quantity =
  | 'on_hand'
  | 'available'
  | 'reserved'
  | 'inbound'
  | 'unfulfillable';

const QUANTITIES: Quantity[] = [
  'on_hand',
  'available',
  'reserved',
  'inbound',
  'unfulfillable',
];

/** A sum over the warehouses, or null when none of them reported one. */
const total = (warehouses: InventoryWarehouse[], key: Quantity) =>
  warehouses.some(warehouse => warehouse[key] != null)
    ? warehouses.reduce((sum, warehouse) => sum + (warehouse[key] ?? 0), 0)
    : null;

const latest = (warehouses: InventoryWarehouse[]) =>
  warehouses.reduce<string | null>(
    (last, { as_of }) => (as_of && (!last || as_of > last) ? as_of : last),
    null,
  );

function QuantityCells({
  values,
  asOf,
}: {
  values: Record<Quantity, number | null>;
  asOf: string | null;
}) {
  return (
    <>
      {QUANTITIES.map(key => (
        <Cell key={key}>
          <span className="sk:tabular-nums">{count(values[key])}</span>
        </Cell>
      ))}
      <Cell className="sk:whitespace-nowrap">{asOf ? when(asOf) : '-'}</Cell>
    </>
  );
}

/** One row per item, as on the legacy tab; an item several warehouses hold sums them and opens to one row each. */
function InventoryItemRows({ item }: { item: InventoryItem }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const { warehouses } = item;
  const multiple = warehouses.length > 1;
  const title = item.title ?? item.item_id;
  const values = Object.fromEntries(
    QUANTITIES.map(key => [key, total(warehouses, key)]),
  ) as Record<Quantity, number | null>;

  return (
    <>
      <Row
        id={item.item_id}
        onAction={() =>
          navigate(`/app/catalogs/catalog/${encodeURIComponent(item.item_id)}`)
        }
      >
        <Cell>
          <ItemProductCell
            item={{
              _id: item.item_id,
              p_title: title,
              p_sku: item.sku ?? undefined,
              secondary_identifier: item.asin,
            }}
            imageUrl={item.image_url}
            identifiers={[{ label: 'GTIN', value: item.gtin ?? undefined }]}
            showState={false}
            expand={
              multiple ? (
                <RowExpandButton
                  isExpanded={expanded}
                  onExpandedChange={() => setExpanded(open => !open)}
                  aria-label={`${expanded ? 'Hide' : 'Show'} warehouses of ${title}`}
                />
              ) : null
            }
          />
        </Cell>
        <Cell>
          {multiple ? `${warehouses.length} warehouses` : warehouses[0]?.name}
        </Cell>
        <QuantityCells values={values} asOf={latest(warehouses)} />
      </Row>
      {multiple && expanded
        ? warehouses.map(warehouse => (
            <Row
              key={warehouse.location_id}
              id={`${item.item_id}/${warehouse.location_id}`}
              isNested
            >
              <Cell>{null}</Cell>
              <Cell>{warehouse.name}</Cell>
              <QuantityCells values={warehouse} asOf={warehouse.as_of} />
            </Row>
          ))
        : null}
    </>
  );
}

function InventoryV2({ inventory }: { inventory: InventoryV2Data }) {
  const { items, loading, error, pagination } = inventory;

  if (error) {
    return (
      <Card>
        <EmptyState title="Inventory unavailable" description={error} />
      </Card>
    );
  }

  return (
    <TableFrame
      aria-label="Inventory"
      columnCount={8}
      isLoading={loading}
      emptyMessage="No inventory found"
      pagination={pagination}
      header={
        <TableHeader>
          <Column id="item" isRowHeader className="sk:w-[40%]">
            Product
          </Column>
          <Column id="warehouse">Warehouse</Column>
          <Column id="on_hand">On hand</Column>
          <Column id="available">Available</Column>
          <Column id="reserved">Reserved</Column>
          <Column id="inbound">Inbound</Column>
          <Column id="unfulfillable">Unfulfillable</Column>
          <Column id="as_of">Updated</Column>
        </TableHeader>
      }
    >
      {items.map(item => (
        <InventoryItemRows key={item.item_id} item={item} />
      ))}
    </TableFrame>
  );
}

type ProviderDetailV2Props = {
  detail: StorageProviderV2Detail;
  tab: string | null;
  onTabChange: (tab: string) => void;
  /** All V2 tabs show provider data, so all follow the SPDetails tab permission. */
  canView: boolean;
  /** The Inventory tab's rows and pages; the page loads them only while that tab is open. Absent when the SPInventory
   * tab permission hides it, which also hides the tab. */
  inventory?: InventoryV2Data;
};

/** EN-4200: the read-only Storage Provider detail served from V2. */
export function ProviderDetailV2({
  detail,
  tab,
  onTabChange,
  canView,
  inventory,
}: ProviderDetailV2Props) {
  const tabs = DETAIL_V2_TABS.filter(
    item => item.id !== 'inventory' || inventory !== undefined,
  );
  const current = tabs.find(item => item.id === tab)?.id ?? tabs[0].id;

  return (
    <div className="sk:flex sk:flex-col sk:gap-4">
      {canView && detail.provider.routing_paused_at ? (
        <div className="sk:flex sk:items-center sk:gap-2">
          <RoutingPausedBadge pause={detail.provider} />
          <span>{routingPausedText(detail.provider)}</span>
        </div>
      ) : null}
      <TabPills
        label="Storage provider sections"
        selectedKey={current}
        onSelectionChange={onTabChange}
        items={tabs.map(item => ({ ...item }))}
      />
      {canView && current === 'details' ? <SummaryV2 detail={detail} /> : null}
      {!canView ? (
        <Card>
          <EmptyState
            title="No access"
            description="You do not have access to this storage provider."
          />
        </Card>
      ) : current === 'inventory' && inventory ? (
        <InventoryV2 inventory={inventory} />
      ) : (
        <DetailsV2 detail={detail} />
      )}
    </div>
  );
}
